import { createAsyncThunk } from "@reduxjs/toolkit"
import axios, { AxiosResponse } from "axios"
import { hideTransparentPreloaderAction, showTransparentPreloaderAction } from "../application/slice"
import { setTokenAction } from "../auth/slice"
import { getProfileDetailsThunk } from "../onboarding/thunk"
import { getCandidateData } from "../superadmin/thunks"
import { TDeleteFileApiResponse, TDeleteUploadedFileResponse, TFileMakeARequestUploadsResponse, TFileUploadsResponse } from "./types"

/**
 * POST method
 * uploadsCandidateDocumentThunk - Uploads the candidates documents
 */
export const uploadsCandidateDocumentThunk = createAsyncThunk(
    'uploads/uploadsCandidateDocumentThunk',
    async (
      { data, isSuperAdmin, isImpersonating = false }: { data: FormData; isSuperAdmin: string | number; isImpersonating?: boolean },
      { dispatch, rejectWithValue }
    ) => {
      try {
        const response: AxiosResponse<TFileUploadsResponse> = await axios.post(`/v1/uploads/documents`, data)
        await dispatch(setTokenAction(response.data.data.token))
        if (isSuperAdmin !== "1") {
          await dispatch(getProfileDetailsThunk())
        }else if(isSuperAdmin === "1"){
          // If the request is from super admin, we don't need to get profile details again because it was replaced with super admin profile details
          const candidateUuid = data.get("candidate_uuid") as string || "";
          if(isImpersonating === true){ 
            await dispatch(getProfileDetailsThunk())
          } else {
            await dispatch(getCandidateData({ candidate_id: candidateUuid }));
          }
        }
        return response.data
      } catch (err) {
        return rejectWithValue((err as any).response.data || err)
      }
    }
)

/**
 * DELETE method
 * deleteCandidateDocumentThunk - delete the candidates documents
 */
export const deleteCandidateDocumentThunk = createAsyncThunk(
  'uploads/deleteCandidateDocumentThunk',
  async ({ document_type, candidate_uuid = "", isImpersonating = false }: { document_type: string; candidate_uuid?: string | null; isImpersonating?: boolean }, { dispatch, rejectWithValue }) => {
    try {
      const payload = candidate_uuid === "" 
        ? { document_type } 
        : { document_type, candidate_uuid };

      const response: AxiosResponse<TDeleteUploadedFileResponse> = await axios.delete(`/v1/uploads/documents`, {
        data: payload
      });
      
      await dispatch(setTokenAction(response.data.data.token))
      if(candidate_uuid === ""){ // Only call if candidate_uuid is empty, it means it was called from candidate login not from super admin
        // If called from super admin, we don't need to get profile details again because it was replaced with super admin profile details
        await dispatch(getProfileDetailsThunk())
      }else if(candidate_uuid !== ""){       
        
        if(isImpersonating) {
          await dispatch(getProfileDetailsThunk())
        }else{
          await dispatch(getCandidateData({ candidate_id: candidate_uuid || "" }));
        }
      }
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * POST method
 * uploadsMakeARequestDocumentThunk - Uploads the candidates documents to create a request
 */
export const uploadsMakeARequestDocumentThunk = createAsyncThunk(
  'uploads/uploadsMakeARequestDocumentThunk',
  async (data: FormData, { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<TFileMakeARequestUploadsResponse> = await axios.post(`/v1/candidates/profile-edit-requests-upload-documents`, data)
      await dispatch(setTokenAction(response.data.data.token))
      // await dispatch(getProfileDetailsThunk())
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)
 
/**
 * DELETE method
 * deleteMakeARequestFile - delete the candidates make a request documents
 */
export const deleteMakeARequestFile = createAsyncThunk(
  'uploads/deleteMakeARequestFile',
  async (attachmentId: number, { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<TDeleteFileApiResponse> = await axios.delete(`/v1/candidates/profile-edit-requests-delete-document/${attachmentId}`, {
        data: { attachmentId }
      })
      await dispatch(setTokenAction(response.data.data.token))
      return response.data
    } catch (err) {
      return rejectWithValue((err as any).response.data || err)
    }
  }
)

/**
 * POST method
 * updateDocumentDateThunk - Update date for candidates documents
 */
export const updateDocumentDateThunk = createAsyncThunk(
    'uploads/updateDocumentDateThunk',
    async ({ document_type, date, candidate_uuid, isAdmin }: { document_type: string; date: string; candidate_uuid?: string; isAdmin?: boolean }, { dispatch, rejectWithValue }) => {
      try {
        const payload = { document_type, date, candidate_uuid }

        const response: AxiosResponse<TFileUploadsResponse> = await axios.post(`/v1/uploads/documents/date`, payload);
        await getCandidateData({ candidate_id: candidate_uuid })
        await dispatch(setTokenAction(response.data.data.token))
        dispatch(showTransparentPreloaderAction())
        if(isAdmin){
          await dispatch(getCandidateData({ candidate_id: candidate_uuid || "" }));
        } else {
          await dispatch(getProfileDetailsThunk());
        }
        dispatch(hideTransparentPreloaderAction())
        return response.data
      } catch (err) {
        return rejectWithValue((err as any).response.data || err)
      }
    }
)